import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Building,
  Camera,
  Save,
  Globe,
  Mail,
  Phone,
  MapPin,
  FileText,
  Users,
  UserCheck,
  Shield,
  Plus,
  Trash2,
  Edit2,
  Eye
} from 'lucide-react';
import { useEmpresa } from '../contexts/EmpresaContext';
import api from '../api/config';
import CustomSelect from './CustomSelect';
import '../styles/ConfiguracionEmpresa.css';

const ConfiguracionEmpresa = () => {
  const { empresaActiva } = useEmpresa();
  const { user, isSuperAdmin } = useSelector(state => state.auth);

  // Obtener el rol del usuario desde la empresa activa
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;

  // Solo administradores pueden gestionar administradores de empresa
  const canManageAdmins = isSuperAdmin || userRole === 'admin' || userRole === 'administrador';
  const [editMode, setEditMode] = useState(false);
  const [logoPreview, setLogoPreview] = useState('/logo-paperboat.png');
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const [hasChanges, setHasChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' or 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [administradores, setAdministradores] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]); // Todos los miembros del equipo
  const [teamStats, setTeamStats] = useState({ usuarios: 0, admins: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit', 'add', 'promote'
  const [addMode, setAddMode] = useState('create'); // 'create' or 'promote'
  const [editingAdmin, setEditingAdmin] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    department: ''
  });
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  // Ocultar header cuando modales están abiertos
  useEffect(() => {
    if (showAdminModal || showFullscreenImage || showDeleteModal) {
      document.body.classList.add('admin-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('admin-modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('admin-modal-open');
      document.body.style.overflow = '';
    };
  }, [showAdminModal, showFullscreenImage, showDeleteModal]);

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  // Cargar datos reales de administradores y equipo
  useEffect(() => {
    const loadTeamData = async () => {
      try {
        setLoading(true);

        // PRIORIZAR empresaActiva del contexto (empresa seleccionada)
        let empresaId = null;

        if (empresaActiva && empresaActiva.id) {
          empresaId = empresaActiva.id;
          console.log('✅ EmpresaId desde empresaActiva (PRIORIDAD):', empresaId);
        }

        // Fallback to localStorage
        if (!empresaId) {
          empresaId = localStorage.getItem('empresaId');
          console.log('   Fallback: EmpresaId desde localStorage:', empresaId);
        }

        if (!empresaId) {
          empresaId = localStorage.getItem('companyId');
          console.log('   Fallback: EmpresaId desde companyId:', empresaId);
        }

        console.log('🔄 Cargando datos del equipo para empresa:', empresaId);
        console.log('   empresaActiva:', empresaActiva);

        if (!empresaId) {
          console.warn('⚠️ No se pudo obtener el ID de empresa');
          setLoading(false);
          return;
        }

        // Cargar usuarios del equipo (enviar companyId como query param)
        const teamResponse = await api.get(`/team/members?companyId=${empresaId}`);
        console.log('👥 Respuesta completa:', teamResponse);
        console.log('👥 Datos del equipo:', teamResponse.data);

        // El backend retorna { success: true, data: members }
        const members = teamResponse.data?.data || teamResponse.data;

        if (members && Array.isArray(members)) {
          const admins = members.filter(user => user.systemRole === 'admin');
          const totalUsers = members.length;

          setAdministradores(admins);
          setTeamMembers(members); // Guardar todos los miembros
          setTeamStats({
            usuarios: totalUsers,
            admins: admins.length
          });

          console.log(`✅ Cargados ${admins.length} administradores y ${totalUsers} usuarios totales`);
          console.log('   Administradores:', admins);
          console.log('   Todos los miembros:', members);
        }
      } catch (error) {
        console.error('❌ Error cargando datos del equipo:', error);
        console.error('   Error response:', error.response);
        setAdministradores([]);
        setTeamStats({ usuarios: 0, admins: 0 });
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [empresaActiva]);

  const [datosEmpresa, setDatosEmpresa] = useState({
    nombreFantasia: '',
    razonSocial: '',
    rut: '',
    giro: '',
    direccion: '',
    telefono: '',
    email: '',
    website: '',
    descripcion: '',
    monedaDefault: 'CLP',
    margenMinimo: 12,
    diasPagoDefault: 30
  });
  const [originalData, setOriginalData] = useState({});

  // Cargar configuración real de la empresa desde la base de datos
  useEffect(() => {
    const loadCompanyConfig = async () => {
      try {
        console.log('🔄 Iniciando carga de configuración de empresa...');
        console.log('🔄 empresaActiva del contexto:', empresaActiva);

        let empresaId = null;

        // PRIORIZAR empresaActiva del contexto (empresa seleccionada)
        if (empresaActiva && empresaActiva.id) {
          empresaId = empresaActiva.id;
          console.log('✅ EmpresaId desde empresaActiva (PRIORIDAD):', empresaId);
        }

        // Si no hay empresaActiva, intentar obtener de my-companies
        if (!empresaId) {
          try {
            const companiesResponse = await api.get('/empresas/my-companies');
            console.log('🏢 Empresas del usuario:', companiesResponse.data);

            if (companiesResponse.data && companiesResponse.data.length > 0) {
              const userCompany = companiesResponse.data[0];
              empresaId = userCompany.companyId || userCompany.Company?.id;
              console.log('✅ EmpresaId obtenido de my-companies:', empresaId);
            }
          } catch (error) {
            console.warn('⚠️ Error obteniendo empresas del usuario:', error.message);
          }
        }

        // Último recurso: localStorage
        if (!empresaId) {
          empresaId = localStorage.getItem('empresaId');
          console.log('📦 EmpresaId de localStorage:', empresaId);
        }

        if (!empresaId) {
          empresaId = localStorage.getItem('companyId');
          console.log('📦 EmpresaId de companyId localStorage:', empresaId);
        }

        if (!empresaId) {
          console.warn('⚠️ No se pudo obtener el ID de empresa para cargar configuración');
          return;
        }

        console.log('📋 Cargando configuración de empresa ID:', empresaId);

        // Ahora cargar los detalles completos de la empresa
        const response = await api.get(`/empresas/${empresaId}`);
        console.log('✅ Configuración de empresa:', response.data);

        const company = response.data;

        const companyData = {
          nombreFantasia: company.tradeName || '',
          razonSocial: company.legalName || '',
          rut: company.taxId || '',
          giro: company.businessActivity || '',
          direccion: company.address || '',
          telefono: company.phone || '',
          email: company.email || '',
          website: company.website || '',
          descripcion: company.description || '',
          monedaDefault: company.defaultCurrency || 'CLP',
          margenMinimo: company.minimumMargin || 12,
          diasPagoDefault: company.defaultPaymentDays || 30
        };

        console.log('✅ Datos mapeados:', companyData);

        setDatosEmpresa(companyData);
        setOriginalData(companyData);

        // Cargar logo si existe
        if (company.logo) {
          console.log('📸 Logo cargado:', company.logo);
          setLogoPreview(company.logo);
        }

        // Guardar el empresaId correcto en localStorage para futuras llamadas
        localStorage.setItem('empresaId', empresaId);
      } catch (error) {
        console.error('❌ Error cargando configuración de empresa:', error);
        console.error('   Error response:', error.response);
      }
    };

    loadCompanyConfig();
  }, [empresaActiva]);

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (máx 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setModalType('error');
        setModalMessage('El archivo es demasiado grande. Máximo 5MB.');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setModalType('error');
        setModalMessage('Tipo de archivo no válido. Solo se permiten imágenes (JPEG, PNG, GIF, WebP, SVG).');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      // Mostrar preview inmediatamente
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      try {
        // Obtener empresaId
        let empresaId = localStorage.getItem('empresaId');
        if (!empresaId && empresaActiva) {
          empresaId = empresaActiva.id;
        }

        if (!empresaId) {
          throw new Error('No se pudo obtener el ID de empresa');
        }

        console.log('📤 Subiendo logo para empresa:', empresaId);

        // Crear FormData para enviar el archivo
        const formData = new FormData();
        formData.append('logo', file);

        // Subir logo al servidor
        const response = await api.post(`/empresas/${empresaId}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('✅ Logo subido exitosamente:', response.data);

        // Actualizar preview con la URL real
        if (response.data.logoUrl) {
          setLogoPreview(response.data.logoUrl);

          // Actualizar datos de empresa
          setDatosEmpresa(prev => ({
            ...prev,
            logo: response.data.logoUrl
          }));
        }

        setModalType('success');
        setModalMessage('Logo actualizado exitosamente');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);

      } catch (error) {
        console.error('❌ Error subiendo logo:', error);
        setModalType('error');
        setModalMessage(error.response?.data?.message || 'Error al subir el logo');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
      }
    }
  };

  const handleSave = async () => {
    try {
      // Obtener el ID de empresa de múltiples fuentes posibles
      let empresaId = localStorage.getItem('empresaId');

      if (!empresaId && empresaActiva) {
        empresaId = empresaActiva.id;
      }

      if (!empresaId) {
        empresaId = localStorage.getItem('companyId');
      }

      if (!empresaId) {
        setModalType('error');
        setModalMessage('No se pudo determinar la empresa activa');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      console.log('💾 Guardando datos de empresa:', empresaId);
      console.log('   Datos a guardar:', datosEmpresa);

      const response = await api.put(`/empresas/${empresaId}/config`, datosEmpresa);

      console.log('✅ Respuesta del servidor:', response.data);

      setOriginalData(datosEmpresa);
      setModalType('success');
      setModalMessage('Configuración guardada exitosamente');
      setShowModal(true);
      setEditMode(false);
      setHasChanges(false);

      // Cerrar modal después de 3 segundos
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    } catch (error) {
      console.error('❌ Error al guardar configuración:', error);
      console.error('   Error response:', error.response);
      setModalType('error');
      setModalMessage(error.response?.data?.message || 'Error al guardar la configuración');
      setShowModal(true);
      setTimeout(() => {
        setShowModal(false);
      }, 3000);
    }
  };

  const handleInputChange = (field, value) => {
    setDatosEmpresa(prev => ({
      ...prev,
      [field]: value
    }));

    // Verificar si hay cambios comparando con los datos originales
    const newData = { ...datosEmpresa, [field]: value };
    const hasAnyChanges = Object.keys(newData).some(
      key => newData[key] !== originalData[key]
    );
    setHasChanges(hasAnyChanges);
  };

  const handleCancel = () => {
    setDatosEmpresa(originalData);
    setEditMode(false);
    setHasChanges(false);
  };

  const handleViewAdmin = (admin) => {
    setSelectedAdmin(admin);
    setModalMode('view');
    setShowAdminModal(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setEditingAdmin({
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      email: admin.email || '',
      phone: admin.phone || '',
      position: admin.position || '',
      department: admin.department || ''
    });
    setModalMode('edit');
    setShowAdminModal(true);
  };

  const handleSaveAdmin = async () => {
    try {
      console.log('💾 Guardando cambios de administrador:', selectedAdmin.id);

      // Obtener empresaId
      let empresaId = localStorage.getItem('empresaId');
      if (!empresaId && empresaActiva) {
        empresaId = empresaActiva.id;
      }

      const response = await api.put(`/team/members/${selectedAdmin.id}`, {
        companyId: empresaId,
        firstName: editingAdmin.firstName,
        lastName: editingAdmin.lastName,
        email: editingAdmin.email,
        phone: editingAdmin.phone,
        position: editingAdmin.position,
        department: editingAdmin.department,
        role: 'admin' // Mantener como admin
      });

      console.log('✅ Administrador actualizado:', response.data);

      // Actualizar la lista local
      setAdministradores(prev =>
        prev.map(a =>
          a.id === selectedAdmin.id
            ? { ...a, ...editingAdmin }
            : a
        )
      );

      setModalType('success');
      setModalMessage('Administrador actualizado exitosamente');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);

      // Cerrar modal
      setShowAdminModal(false);
      setSelectedAdmin(null);
      setModalMode('view');
    } catch (error) {
      console.error('❌ Error actualizando administrador:', error);
      setModalType('error');
      setModalMessage('Error al actualizar el administrador');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    }
  };

  const handleAddAdmin = async () => {
    try {
      // Validar campos requeridos
      if (!editingAdmin.firstName || !editingAdmin.email) {
        setModalType('error');
        setModalMessage('El nombre y el email son obligatorios');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editingAdmin.email)) {
        setModalType('error');
        setModalMessage('El formato del email no es válido');
        setShowModal(true);
        setTimeout(() => setShowModal(false), 3000);
        return;
      }

      console.log('➕ Agregando nuevo administrador');

      // Obtener empresaId
      let empresaId = localStorage.getItem('empresaId');
      if (!empresaId && empresaActiva) {
        empresaId = empresaActiva.id;
      }

      if (!empresaId) {
        throw new Error('No se pudo obtener el ID de empresa');
      }

      const response = await api.post('/team/members', {
        companyId: empresaId,
        firstName: editingAdmin.firstName,
        lastName: editingAdmin.lastName,
        email: editingAdmin.email,
        phone: editingAdmin.phone,
        position: editingAdmin.position,
        role: 'admin'
      });

      console.log('✅ Administrador agregado:', response.data);

      // Actualizar la lista local
      const newAdmin = response.data.data || response.data;
      setAdministradores(prev => [...prev, newAdmin]);
      setTeamStats(prev => ({ ...prev, admins: prev.admins + 1 }));

      setModalType('success');
      setModalMessage('Administrador agregado exitosamente');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);

      // Cerrar modal
      setShowAdminModal(false);
      setSelectedAdmin(null);
      setModalMode('view');
    } catch (error) {
      console.error('❌ Error agregando administrador:', error);
      setModalType('error');
      setModalMessage(error.response?.data?.message || 'Error al agregar el administrador');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    }
  };

  const handlePromoteToAdmin = async (member) => {
    try {
      console.log('⬆️ Promoviendo a administrador:', member.email);

      // Obtener empresaId
      let empresaId = localStorage.getItem('empresaId');
      if (!empresaId && empresaActiva) {
        empresaId = empresaActiva.id;
      }

      if (!empresaId) {
        throw new Error('No se pudo obtener el ID de empresa');
      }

      const response = await api.put(`/team/members/${member.id}`, {
        companyId: empresaId,
        role: 'admin'
      });

      console.log('✅ Usuario promovido a administrador:', response.data);

      // Actualizar listas locales
      setAdministradores(prev => [...prev, { ...member, systemRole: 'admin' }]);
      setTeamMembers(prev =>
        prev.map(m => m.id === member.id ? { ...m, systemRole: 'admin' } : m)
      );
      setTeamStats(prev => ({ ...prev, admins: prev.admins + 1 }));

      setModalType('success');
      setModalMessage(`${member.firstName || member.username || member.email} ha sido promovido a administrador`);
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);

      // Cerrar modal
      setShowAdminModal(false);
      setModalMode('view');
      setAddMode('create');
    } catch (error) {
      console.error('❌ Error promoviendo a administrador:', error);
      setModalType('error');
      setModalMessage(error.response?.data?.message || 'Error al promover el usuario');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    }
  };

  const handleDeleteAdmin = (admin) => {
    // Validar que no sea el último administrador
    if (administradores.length === 1) {
      setModalType('error');
      setModalMessage('No puedes eliminar al único administrador. Debe haber al menos un administrador en la empresa.');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
      return;
    }

    // Abrir modal de confirmación
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const confirmDeleteAdmin = async () => {
    try {
      console.log('🗑️ Eliminando administrador:', adminToDelete.id);

      // Obtener empresaId
      let empresaId = localStorage.getItem('empresaId');
      if (!empresaId && empresaActiva) {
        empresaId = empresaActiva.id;
      }

      // Cambiar rol de admin a user
      const response = await api.put(`/team/members/${adminToDelete.id}`, {
        companyId: empresaId,
        role: 'user'
      });

      console.log('✅ Administrador removido exitosamente:', response.data);

      // Actualizar la lista local
      setAdministradores(prev => prev.filter(a => a.id !== adminToDelete.id));
      setTeamStats(prev => ({ ...prev, admins: prev.admins - 1 }));

      // Cerrar modal de confirmación
      setShowDeleteModal(false);
      setAdminToDelete(null);

      setModalType('success');
      setModalMessage(`${adminToDelete.firstName} ${adminToDelete.lastName} ya no es administrador`);
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    } catch (error) {
      console.error('❌ Error eliminando administrador:', error);
      setShowDeleteModal(false);
      setAdminToDelete(null);
      setModalType('error');
      setModalMessage('Error al cambiar el rol del administrador');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
    }
  };

  // Estilos para inputs - theme color cuando disabled, blanco cuando editable
  const inputStyle = (isEditable) => ({
    border: `2px solid #000`,
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    fontWeight: '600',
    background: isEditable ? '#FFFFFF' : themeColor,
    backgroundColor: isEditable ? '#FFFFFF' : themeColor,
    color: '#000000',
    width: '100%',
    fontFamily: "'Space Grotesk', sans-serif",
    transition: 'all 0.3s ease',
    opacity: '1',
    outline: 'none',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none'
  });

  return (
    <>
      <style>{`
        .config-input {
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
        }
        .config-input:not(:disabled) {
          background-color: #FFFFFF !important;
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
        }
        .config-input:disabled {
          background-color: ${themeColor} !important;
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
        }
        /* Force all inputs and labels to have black text */
        input, textarea, select, label {
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
        }
        label {
          opacity: 1 !important;
        }
      `}</style>
      <div style={{ backgroundColor: themeColor, minHeight: 'calc(100vh - 80px)', padding: '24px' }}>
      <div className="config-header" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px', marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="header-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Building size={28} />
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0', color: '#000' }}>Configuración de Empresa</h1>
            <p style={{ margin: '4px 0 0 0', color: '#000', fontSize: '14px', opacity: '0.9' }}>Personaliza la información y apariencia de tu empresa</p>
          </div>
        </div>

        <div className="header-actions" style={{ display: 'flex', gap: '12px' }}>
          {canManageAdmins && (
            !editMode ? (
              <button
                onClick={() => setEditMode(true)}
                style={{
                  backgroundColor: themeColor,
                  border: '2px solid #000000',
                  color: '#000000',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Editar Configuración
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  style={{
                    backgroundColor: hasChanges ? themeColor : '#e0e0e0',
                    border: '2px solid #000000',
                    color: '#000000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: hasChanges ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px',
                    opacity: hasChanges ? 1 : 0.6
                  }}
                >
                  <Save size={18} />
                  Guardar Cambios
                </button>
              </>
            )
          )}
        </div>
      </div>

      <div className="config-content" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Logo y Marca */}
        <div className="config-section" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 20px 0', color: '#000' }}>Logo y Marca</h2>
          <div className="logo-section" style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            <div className="logo-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <div
                className="logo-preview"
                style={{ width: '150px', height: '150px', border: '2px solid #000', borderRadius: '12px', overflow: 'hidden', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                onClick={() => setShowFullscreenImage(true)}
              >
                <img src={logoPreview} alt="Logo empresa" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
              {canManageAdmins && (
                <button
                  onClick={() => editMode && document.getElementById('logo-input').click()}
                  disabled={!editMode}
                  className="btn-change-logo"
                  style={{
                    backgroundColor: editMode ? themeColor : '#e0e0e0',
                    color: '#000',
                    border: '2px solid #000',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: editMode ? 'pointer' : 'not-allowed',
                    opacity: editMode ? 1 : 0.6,
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}
                >
                  <Camera size={18} />
                  Cambiar Logo
                </button>
              )}
              <input
                id="logo-input"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: 'none' }}
              />
            </div>

            <div className="brand-fields" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group">
                <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'block' }}>Nombre Comercial</label>
                <input
                  type="text"
                  value={datosEmpresa.nombreFantasia}
                  onChange={(e) => handleInputChange('nombreFantasia', e.target.value)}
                  disabled={!editMode}
                  className="config-input"
                  style={inputStyle(editMode)}
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'block' }}>Descripción de la Empresa</label>
                <textarea
                  value={datosEmpresa.descripcion}
                  onChange={(e) => handleInputChange('descripcion', e.target.value)}
                  disabled={!editMode}
                  rows={3}
                  className="config-input"
                  style={{ ...inputStyle(editMode), resize: 'vertical', minHeight: '80px' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Información Legal */}
        <div className="config-section" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 20px 0', color: '#000' }}>Información Legal</h2>
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FileText size={16} /> Razón Social
              </label>
              <input
                type="text"
                value={datosEmpresa.razonSocial}
                onChange={(e) => handleInputChange('razonSocial', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'block' }}>RUT</label>
              <input
                type="text"
                value={datosEmpresa.rut}
                onChange={(e) => handleInputChange('rut', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'block' }}>Giro Comercial</label>
              <input
                type="text"
                value={datosEmpresa.giro}
                onChange={(e) => handleInputChange('giro', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>
          </div>
        </div>

        {/* Información de Contacto */}
        <div className="config-section" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 20px 0', color: '#000' }}>Información de Contacto</h2>
          <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={16} /> Teléfono
              </label>
              <input
                type="tel"
                value={datosEmpresa.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={16} /> Email
              </label>
              <input
                type="email"
                value={datosEmpresa.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Globe size={16} /> Sitio Web
              </label>
              <input
                type="text"
                value={datosEmpresa.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPin size={16} /> Dirección
              </label>
              <input
                type="text"
                value={datosEmpresa.direccion}
                onChange={(e) => handleInputChange('direccion', e.target.value)}
                disabled={!editMode}
                className="config-input"
                style={inputStyle(editMode)}
              />
            </div>
          </div>
        </div>

        {/* Administradores de la Empresa - Solo visible para administradores */}
        {canManageAdmins && (
          <div className="config-section" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: '#000' }}>Administradores de la Empresa</h2>
              <button
                className="btn-add-admin"
                onClick={() => {
                  setModalMode('add');
                  setAddMode('create'); // Por defecto en modo crear
                  setEditingAdmin({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    position: '',
                    department: ''
                  });
                  setShowAdminModal(true);
                }}
                style={{
                  backgroundColor: themeColor,
                  border: '2px solid #000',
                  color: '#000'
                }}
              >
                <Plus size={18} />
                <span>Agregar Administrador</span>
              </button>
            </div>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#000', padding: '20px' }}>Cargando administradores...</p>
          ) : administradores.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#000', padding: '20px' }}>No hay administradores registrados</p>
          ) : (
            <div className="admin-list">
              {administradores.map((admin, index) => (
                <div key={admin.id || index} className="admin-item">
                  <div className="admin-avatar" style={{ backgroundColor: themeColor }}>
                    <Shield size={20} />
                  </div>
                  <div className="admin-info">
                    <h4>
                      {admin.firstName && admin.lastName
                        ? `${admin.firstName} ${admin.lastName}`
                        : admin.username || admin.email?.split('@')[0]}
                    </h4>
                    <p>
                      {admin.email}
                    </p>
                    <span className="admin-role" style={{ backgroundColor: themeColor }}>
                      {index === 0 ? 'Administrador Principal' : 'Administrador'}
                    </span>
                  </div>
                  <div className="admin-actions">
                    <button
                      onClick={() => handleViewAdmin(admin)}
                      title="Ver detalles"
                    >
                      <Eye size={16} />
                      Ver
                    </button>
                    <button
                      onClick={() => handleEditAdmin(admin)}
                      title="Editar administrador"
                    >
                      <Edit2 size={16} />
                      Editar
                    </button>
                    <button
                      className="btn-delete-admin"
                      onClick={() => handleDeleteAdmin(admin)}
                      disabled={administradores.length === 1}
                      title={administradores.length === 1 ? 'No puedes eliminar al único administrador' : 'Eliminar administrador'}
                    >
                      <Trash2 size={16} />
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Equipo de la Empresa */}
        <div className="config-section" style={{ background: 'white', border: '2px solid #000', borderRadius: '16px', padding: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 20px 0', color: '#000' }}>Equipo de Trabajo</h2>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#000', padding: '20px' }}>Cargando estadísticas...</p>
          ) : (
            <>
              <div className="team-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                <div className="stat-item" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', background: 'white', border: '2px solid #000', borderRadius: '12px' }}>
                  <Users size={20} />
                  <div>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: '#000', display: 'block' }}>{teamStats.usuarios}</span>
                    <span style={{ fontSize: '14px', color: '#000', opacity: '0.8' }}>Usuarios Activos</span>
                  </div>
                </div>
                <div className="stat-item" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', background: 'white', border: '2px solid #000', borderRadius: '12px' }}>
                  <Shield size={20} />
                  <div>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: '#000', display: 'block' }}>{teamStats.admins}</span>
                    <span style={{ fontSize: '14px', color: '#000', opacity: '0.8' }}>Administradores</span>
                  </div>
                </div>
              </div>
              <p style={{ fontSize: '14px', color: '#000' }}>
                Para gestionar el equipo completo, visita la sección{' '}
                <a href="/equipo" style={{
                  color: '#000',
                  fontWeight: '700',
                  textDecoration: 'underline',
                  textDecorationThickness: '2px',
                  textUnderlineOffset: '3px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: themeColor,
                  border: '2px solid #000',
                  transition: 'all 0.2s ease'
                }}>Equipo</a>
              </p>
            </>
          )}
        </div>

      </div>
    </div>

    {/* Modal de detalles del administrador */}
    {showAdminModal && (
      <div className="admin-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '10px'
      }}>
        <div className="admin-modal-content" style={{
          backgroundColor: 'white',
          border: '3px solid #000',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            {modalMode === 'add' && addMode === 'promote' && (
              <button
                onClick={() => setAddMode('create')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '4px 8px'
                }}
              >
                ←
              </button>
            )}
            <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0, color: '#000' }}>
              {modalMode === 'view' ? 'Detalles del Administrador' : modalMode === 'edit' ? 'Editar Administrador' : 'Agregar Administrador'}
            </h2>
          </div>

          {modalMode === 'add' && addMode === 'create' ? (
            // Modo: Seleccionar entre crear nuevo o promover existente
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                onClick={() => setAddMode('new')}
                style={{
                  backgroundColor: themeColor,
                  border: '2px solid #000',
                  color: '#000',
                  padding: '16px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontFamily: "'Space Grotesk', sans-serif",
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                <div style={{ fontSize: '18px', marginBottom: '4px' }}>Crear Nuevo Usuario</div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Agregar un usuario completamente nuevo al sistema</div>
              </button>

              <div style={{ textAlign: 'center', color: '#666', fontSize: '14px', margin: '8px 0' }}>
                O selecciona un miembro del equipo para promover a administrador:
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '400px',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
                {teamMembers.filter(member => member.systemRole !== 'admin').length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                    No hay miembros disponibles para promover
                  </p>
                ) : (
                  teamMembers
                    .filter(member => member.systemRole !== 'admin')
                    .map((member) => (
                      <div
                        key={member.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '56px 1fr auto',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '16px',
                          border: '2px solid #000',
                          borderRadius: '10px',
                          backgroundColor: 'white',
                          transition: 'all 0.2s ease',
                          minHeight: '80px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = themeColor;
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        <div style={{
                          width: '56px',
                          height: '56px',
                          backgroundColor: '#f0f0f0',
                          border: '2px solid #000',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '18px',
                          flexShrink: 0
                        }}>
                          {member.firstName?.charAt(0)?.toUpperCase() || member.username?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                          {member.lastName?.charAt(0)?.toUpperCase() || ''}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            fontWeight: '700',
                            fontSize: '16px',
                            color: '#000',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {member.firstName && member.lastName
                              ? `${member.firstName} ${member.lastName}`
                              : member.username || member.email?.split('@')[0]}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: '#666',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {member.email}
                          </div>
                          <span style={{
                            backgroundColor: '#e0e0e0',
                            color: '#000',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            border: '1px solid #000',
                            display: 'inline-block',
                            marginTop: '2px',
                            width: 'fit-content'
                          }}>
                            Miembro
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePromoteToAdmin(member);
                          }}
                          style={{
                            backgroundColor: themeColor,
                            border: '2px solid #000',
                            color: '#000',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontFamily: "'Space Grotesk', sans-serif",
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.05)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          Promover
                        </button>
                      </div>
                    ))
                )}
              </div>
            </div>
          ) : (modalMode === 'add' && addMode === 'new') || modalMode === 'edit' || modalMode === 'view' ? (
            // Modo: Formulario (crear nuevo, editar, o vista)
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                Nombre
              </label>
              {(modalMode === 'edit' || (modalMode === 'add' && addMode === 'new')) ? (
                <input
                  type="text"
                  value={editingAdmin.firstName}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, firstName: e.target.value })}
                  placeholder="Ingrese el nombre"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: '600'
                  }}
                />
              ) : (
                <p style={{ margin: 0, fontSize: '16px', color: '#000' }}>
                  {selectedAdmin?.firstName} {selectedAdmin?.lastName}
                </p>
              )}
            </div>

            {(modalMode === 'edit' || (modalMode === 'add' && addMode === 'new')) && (
              <div>
                <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                  Apellido (Opcional)
                </label>
                <input
                  type="text"
                  value={editingAdmin.lastName}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, lastName: e.target.value })}
                  placeholder="Ingrese el apellido"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: '600'
                  }}
                />
              </div>
            )}

            <div>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                Email
              </label>
              {(modalMode === 'edit' || (modalMode === 'add' && addMode === 'new')) ? (
                <input
                  type="email"
                  value={editingAdmin.email}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: '600'
                  }}
                />
              ) : (
                <p style={{ margin: 0, fontSize: '16px', color: '#000' }}>
                  {selectedAdmin?.email}
                </p>
              )}
            </div>

            <div>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                Teléfono (Opcional)
              </label>
              {(modalMode === 'edit' || (modalMode === 'add' && addMode === 'new')) ? (
                <input
                  type="tel"
                  value={editingAdmin.phone}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, phone: e.target.value })}
                  placeholder="+56 9 XXXX XXXX"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #000',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: '600'
                  }}
                />
              ) : (
                <p style={{ margin: 0, fontSize: '16px', color: '#000' }}>
                  {selectedAdmin?.phone || 'No especificado'}
                </p>
              )}
            </div>

            <div>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                Cargo (Opcional)
              </label>
              {(modalMode === 'edit' || (modalMode === 'add' && addMode === 'new')) ? (
                <CustomSelect
                  name="position"
                  value={editingAdmin.position}
                  onChange={(e) => setEditingAdmin({ ...editingAdmin, position: e.target.value })}
                  options={[
                    { value: '', label: 'Seleccione un cargo' },
                    { value: 'Gerente General', label: 'Gerente General' },
                    { value: 'Gerente de Operaciones', label: 'Gerente de Operaciones' },
                    { value: 'Gerente de Ventas', label: 'Gerente de Ventas' },
                    { value: 'Gerente Comercial', label: 'Gerente Comercial' },
                    { value: 'Gerente de Compras', label: 'Gerente de Compras' },
                    { value: 'Jefe de Proyecto', label: 'Jefe de Proyecto' },
                    { value: 'Contador', label: 'Contador' },
                    { value: 'Analista', label: 'Analista' },
                    { value: 'Coordinador', label: 'Coordinador' },
                    { value: 'Supervisor', label: 'Supervisor' },
                    { value: 'Ejecutivo de Ventas', label: 'Ejecutivo de Ventas' },
                    { value: 'Vendedor', label: 'Vendedor' },
                    { value: 'Asistente', label: 'Asistente' }
                  ]}
                  placeholder="Seleccione un cargo"
                />
              ) : (
                <p style={{ margin: 0, fontSize: '16px', color: '#000' }}>
                  {selectedAdmin?.position || 'No especificado'}
                </p>
              )}
            </div>

            {modalMode === 'view' && (
              <div>
                <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                  Departamento
                </label>
                <p style={{ margin: 0, fontSize: '16px', color: '#000' }}>
                  {selectedAdmin?.department || 'No especificado'}
                </p>
              </div>
            )}

            <div>
              <label style={{ fontSize: '14px', fontWeight: '700', color: '#000', display: 'block', marginBottom: '6px' }}>
                Rol
              </label>
              <span style={{
                backgroundColor: themeColor,
                color: '#000',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                border: '2px solid #000',
                display: 'inline-block'
              }}>
                Administrador
              </span>
            </div>
          </div>
          ) : null}

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            {modalMode === 'add' && addMode === 'create' ? (
              <button
                onClick={() => {
                  setShowAdminModal(false);
                  setModalMode('view');
                  setAddMode('create');
                }}
                style={{
                  backgroundColor: 'white',
                  border: '2px solid #000',
                  color: '#000',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '14px'
                }}
              >
                Cerrar
              </button>
            ) : modalMode === 'add' && addMode === 'new' ? (
              <>
                <button
                  onClick={() => setAddMode('create')}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  ← Volver
                </button>
                <button
                  onClick={handleAddAdmin}
                  style={{
                    backgroundColor: themeColor,
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Crear Administrador
                </button>
              </>
            ) : modalMode === 'edit' ? (
              <>
                <button
                  onClick={() => {
                    setShowAdminModal(false);
                    setSelectedAdmin(null);
                    setModalMode('view');
                  }}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveAdmin}
                  style={{
                    backgroundColor: themeColor,
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Guardar Cambios
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setEditingAdmin({
                      firstName: selectedAdmin.firstName || '',
                      lastName: selectedAdmin.lastName || '',
                      email: selectedAdmin.email || '',
                      phone: selectedAdmin.phone || '',
                      position: selectedAdmin.position || '',
                      department: selectedAdmin.department || ''
                    });
                    setModalMode('edit');
                  }}
                  style={{
                    backgroundColor: themeColor,
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Editar
                </button>
                <button
                  onClick={() => {
                    setShowAdminModal(false);
                    setSelectedAdmin(null);
                    setModalMode('view');
                  }}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #000',
                    color: '#000',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  Cerrar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Modal de éxito/error */}
    {showModal && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          border: '3px solid #000',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            backgroundColor: themeColor,
            borderRadius: '50%',
            border: '3px solid #000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '30px'
          }}>
            {modalType === 'success' ? '✓' : '✗'}
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#000',
            margin: '0 0 12px 0',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            {modalType === 'success' ? '¡Éxito!' : 'Error'}
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#000',
            margin: 0,
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            {modalMessage}
          </p>
        </div>
      </div>
    )}

    {/* Modal de imagen en pantalla completa */}
    {showFullscreenImage && (
      <div
        className="fullscreen-image-modal"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px',
          cursor: 'pointer'
        }}
        onClick={() => setShowFullscreenImage(false)}
      >
        <img
          src={logoPreview}
          alt="Logo empresa"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            cursor: 'pointer'
          }}
        />
      </div>
    )}

    {/* Modal de confirmación de eliminar administrador */}
    {showDeleteModal && adminToDelete && (
      <div
        className="delete-modal-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
      >
        <div
          className="delete-modal-content"
          style={{
            backgroundColor: 'white',
            border: '3px solid #000',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#fee2e2',
            border: '3px solid #dc2626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px',
            color: '#dc2626'
          }}>
            ⚠
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#000',
            margin: '0 0 16px 0',
            fontFamily: "'Space Grotesk', sans-serif"
          }}>
            ¿Eliminar Administrador?
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#000',
            margin: '0 0 24px 0',
            fontFamily: "'Space Grotesk', sans-serif",
            lineHeight: '1.5'
          }}>
            ¿Estás seguro de eliminar a <strong>{adminToDelete.firstName} {adminToDelete.lastName}</strong> como administrador?
            <br /><br />
            Su rol cambiará a "usuario" pero mantendrá su acceso a la empresa.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setAdminToDelete(null);
              }}
              style={{
                backgroundColor: 'white',
                border: '2px solid #000',
                color: '#000',
                padding: '12px 24px',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={confirmDeleteAdmin}
              style={{
                backgroundColor: '#dc2626',
                border: '2px solid #000',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
            >
              Sí, Eliminar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ConfiguracionEmpresa;